import { Fragment, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useOutlet } from "react-router-dom";
import { isAgentAllowedPath, type OrganizationMemberRoleName } from "@multi-chat/shared";
import { useTranslation } from "react-i18next";
import {
  Menu as MenuIcon,
  PanelLeftClose as PanelCollapseIcon,
  PanelLeftOpen as PanelExpandIcon,
  X as CloseIcon,
} from "lucide-react";
import { useOrganizations } from "../../features/organizations/organization-context.js";
import {
  OrganizationUnavailableState,
  useSuspendedOrganizationId,
} from "../../features/organizations/organization-unavailable.js";
import { realMembershipRoleOf } from "../../features/organizations/organization-selection.js";
import { clearOrganizationSuspended } from "../../features/organizations/organization-suspended-signal.js";
import { OrganizationSwitcher } from "../../features/organizations/OrganizationSwitcher/OrganizationSwitcher.js";
import { AppBootstrapLoader } from "./AppBootstrapLoader.js";
import { AppNotice } from "../common/AppNotice.js";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "../ui/sheet/index.js";
import { SimpleTooltip, TooltipProvider } from "../ui/tooltip/index.js";
import { cn } from "../../lib/utils.js";
import { AnimatePresence, motion, useReducedMotion } from "../../lib/motion.js";
import { AppBrandMark } from "./AppBrandMark.js";
import { AppRailAccountMenu } from "./AppRailAccountMenu.js";
import { AppRailNav } from "./AppRailNav.js";
import { InboxRealtimeProvider } from "../../features/inbox/InboxRealtimeProvider.js";
import { NotificationProvider } from "../../features/notifications/notification-context.js";
import { NotificationStack } from "../../features/notifications/NotificationStack.js";
import { ContactImportJobPoller } from "../../features/notifications/ContactImportJobPoller.js";
import { FeedBotMark } from "../brand/FeedBotMark.js";
import {
  CHARCOAL_FOREST_SURFACE,
  RAIL_FOCUS,
  RAIL_HEADER_CONTROL,
  RAIL_SCROLL,
} from "./rail-compact.js";
import { routeContentMotion } from "./route-content-motion.js";
import { routeTransitionKey } from "./route-workspace-key.js";
import { useMinWidth } from "./use-min-width.js";
import { useSidebarCollapsedPreference } from "./use-sidebar-collapsed-preference.js";

const DESKTOP_MIN_PX = 1200;
const TABLET_MIN_PX = 768;

/** Charcoal Forest rail surface shared by the desktop rail and the mobile drawer. Position comes from each host. */
const RAIL_SURFACE = `font-feedbot ${CHARCOAL_FOREST_SURFACE} text-[14px] leading-[1.5] text-[#effbf7] before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-[linear-gradient(90deg,transparent,#55bca6,transparent)] before:content-['']`;

/** Phone header: forest surface, with a mint hairline toward the content. */
const MOBILE_TOP_BAR =
  "font-feedbot relative z-[5] flex min-h-14 shrink-0 items-center gap-3 bg-linear-to-r from-nav to-nav-mid px-3 py-2 pt-[max(0.5rem,env(safe-area-inset-top,0px))] ps-[max(0.75rem,env(safe-area-inset-left,0px))] pe-[max(0.75rem,env(safe-area-inset-right,0px))] text-[#effbf7] shadow-[0_6px_20px_rgba(9,30,27,0.12)] after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-[linear-gradient(90deg,transparent,#55bca680,transparent)] after:content-['']";

type RailMembership = React.ComponentProps<typeof AppRailAccountMenu>["membership"];

function AppRail({
  compact,
  headerControl,
  role,
  user,
  membership,
  onLogout,
  onDisplayNameSaved,
  onNavigate,
  footerClassName,
}: {
  compact: boolean;
  /** Collapse or close control, drawn on the physical left of the header. */
  headerControl: React.ReactNode;
  role: OrganizationMemberRoleName;
  user: { email: string };
  membership: RailMembership;
  onLogout: () => void;
  onDisplayNameSaved: (organizationId: number, displayName: string) => void;
  onNavigate?: () => void;
  footerClassName?: string;
}): React.JSX.Element {
  return (
    <>
      <div
        className={cn(
          "flex min-h-[84px] shrink-0 border-b border-white/[0.09]",
          compact
            ? "mx-2 flex-col-reverse items-center justify-center gap-2"
            : "mx-[18px] items-center justify-between gap-2.5 rtl:flex-row-reverse",
        )}
      >
        {headerControl}
        <AppBrandMark compact={compact} />
      </div>
      <div
        className={cn(
          "min-h-0 flex-1 overflow-x-hidden overflow-y-auto py-4",
          compact ? "px-2" : "px-3",
          RAIL_SCROLL,
        )}
      >
        <div className={compact ? "mb-[18px]" : "mx-px mb-5"}>
          <OrganizationSwitcher compact={compact} onSelect={onNavigate} />
        </div>
        <AppRailNav compact={compact} role={role} onNavigate={onNavigate} />
      </div>
      <div
        className={cn(
          "flex min-w-0 shrink-0 flex-col gap-2.5 border-t border-white/10 p-[13px]",
          footerClassName,
        )}
      >
        <AppRailAccountMenu
          user={user}
          membership={membership}
          compact={compact}
          onLogout={onLogout}
          onDisplayNameSaved={onDisplayNameSaved}
        />
      </div>
    </>
  );
}

export function AuthenticatedShell({
  user,
  onLogout,
  onDisplayNameSaved,
}: {
  user: { email: string };
  onLogout: () => void;
  onDisplayNameSaved: (organizationId: number, displayName: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { selected } = useOrganizations();
  const location = useLocation();
  const navigate = useNavigate();
  // Snapshot element rather than <Outlet />: AnimatePresence must keep rendering
  // the outgoing page while it exits, not the newly matched one.
  const outlet = useOutlet();
  const reduceMotion = useReducedMotion();
  const isDesktop = useMinWidth(DESKTOP_MIN_PX);
  const showRail = useMinWidth(TABLET_MIN_PX);
  const [desktopCollapsed, setDesktopCollapsed] = useSidebarCollapsedPreference();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);

  const isInbox =
    location.pathname === "/inbox" ||
    location.pathname.startsWith("/inbox/") ||
    location.pathname === "/contacts" ||
    location.pathname.startsWith("/contacts/");
  const isPostComments =
    location.pathname === "/post-comments" ||
    location.pathname.startsWith("/post-comments/");
  const isCampaigns =
    location.pathname === "/campaigns" ||
    location.pathname.startsWith("/campaigns/");
  const isReports =
    location.pathname === "/reports" ||
    location.pathname.startsWith("/reports/");
  const isSettings =
    location.pathname === "/settings" ||
    location.pathname.startsWith("/settings/");
  const isChannels =
    location.pathname === "/channels" ||
    location.pathname.startsWith("/channels/");
  const isFullHeightWorkspace =
    isInbox || isPostComments || isCampaigns || isReports || isSettings || isChannels;
  /** Labels only when desktop AND user prefers expanded. Tablet stays compact without writing storage. */
  const showLabels = !desktopCollapsed && isDesktop;

  useEffect(() => {
    const width = !showRail ? "0px" : showLabels ? "254px" : "74px";
    document.documentElement.style.setProperty("--app-rail-width", width);
    return () => {
      document.documentElement.style.removeProperty("--app-rail-width");
    };
  }, [showRail, showLabels]);

  useEffect(() => {
    const sync = () => {
      setTemplateEditorOpen(document.documentElement.hasAttribute("data-template-editor-open"));
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-template-editor-open"],
    });
    return () => observer.disconnect();
  }, []);
  const routeMotion = routeContentMotion(reduceMotion ?? false);
  const agentLockedOut =
    selected !== null && selected.role === "AGENT" && !isAgentAllowedPath(location.pathname);
  const suspendedOrganizationId = useSuspendedOrganizationId();
  const organizationSuspended =
    selected !== null &&
    (selected.status === "SUSPENDED" || suspendedOrganizationId === selected.id);
  const platformAdminAccess = selected?.access === "PLATFORM_ADMIN";

  useEffect(() => {
    if (suspendedOrganizationId !== null && selected !== null && suspendedOrganizationId !== selected.id) {
      clearOrganizationSuspended();
    }
  }, [selected, suspendedOrganizationId]);

  if (!selected) {
    return <AppBootstrapLoader label={t("app.loading")} />;
  }

  // One page tree per organization: a switch remounts it, so no page can keep
  // the previous organization's data on screen while the new one loads.
  const workspaceOutlet = <Fragment key={selected.id}>{outlet}</Fragment>;

  const membership: RailMembership = {
    organizationId: selected.id,
    role: selected.role,
    membershipRole: realMembershipRoleOf(selected),
    displayName: selected.memberDisplayName,
    platformAdminAccess,
  };
  const collapseLabel = showLabels ? t("app.sidebar.collapse") : t("app.sidebar.expand");
  const collapseControl = isDesktop ? (
    <SimpleTooltip content={collapseLabel}>
      <button
        type="button"
        className={cn(
          RAIL_HEADER_CONTROL,
          showLabels ? "h-[30px]" : "h-[26px]",
        )}
        aria-label={collapseLabel}
        aria-expanded={showLabels}
        onClick={() => setDesktopCollapsed(showLabels)}
      >
        {showLabels ? (
          <PanelCollapseIcon size={16} strokeWidth={1.8} className="rtl:-scale-x-100" aria-hidden="true" />
        ) : (
          <PanelExpandIcon size={16} strokeWidth={1.8} className="rtl:-scale-x-100" aria-hidden="true" />
        )}
      </button>
    </SimpleTooltip>
  ) : null;

  return (
    <NotificationProvider>
    <InboxRealtimeProvider>
    <TooltipProvider delayDuration={300}>
      <div className="flex h-dvh overflow-hidden bg-canvas">
        {showRail ? (
          <aside
            className={cn(
              RAIL_SURFACE,
              "sticky inset-y-0 z-[6] hidden h-dvh shrink-0 flex-col overflow-hidden transition-[width] duration-[250ms] ease-[ease] md:flex motion-reduce:transition-none",
              "ltr:shadow-[8px_0_30px_rgba(9,30,27,0.07)] rtl:shadow-[-8px_0_30px_rgba(9,30,27,0.07)]",
              showLabels ? "w-[254px]" : "w-[74px]",
              templateEditorOpen && "pointer-events-none",
            )}
            aria-label={t("app.name")}
          >
            <AppRail
              compact={!showLabels}
              headerControl={collapseControl}
              role={selected.role}
              user={user}
              membership={membership}
              onLogout={onLogout}
              onDisplayNameSaved={onDisplayNameSaved}
            />
          </aside>
        ) : null}

        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {!showRail ? (
            <header className={MOBILE_TOP_BAR}>
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen} modal={false}>
                <SheetTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "inline-grid size-10 shrink-0 place-items-center rounded-[12px] border border-white/10 bg-white/[0.06] text-[#d6efe6] transition-[background-color,border-color,transform] duration-150 ease-out hover:border-white/20 hover:bg-white/10 active:scale-[0.94] active:bg-white/10 motion-reduce:transition-none motion-reduce:active:scale-100",
                      RAIL_FOCUS,
                    )}
                    aria-label={t("app.menu")}
                  >
                    <MenuIcon size={20} strokeWidth={1.8} aria-hidden="true" />
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="start"
                  className={cn(RAIL_SURFACE, "w-[254px] max-w-[85vw] overflow-hidden")}
                  aria-describedby={undefined}
                >
                  <SheetTitle className="sr-only">{t("app.menu")}</SheetTitle>
                  <AppRail
                    compact={false}
                    headerControl={
                      <SheetClose asChild>
                        <button
                          type="button"
                          className={cn(RAIL_HEADER_CONTROL, "h-[30px]")}
                          aria-label={t("app.closeMenu")}
                        >
                          <CloseIcon size={16} strokeWidth={1.8} aria-hidden="true" />
                        </button>
                      </SheetClose>
                    }
                    role={selected.role}
                    user={user}
                    membership={membership}
                    onLogout={onLogout}
                    onDisplayNameSaved={onDisplayNameSaved}
                    onNavigate={() => setMobileOpen(false)}
                    footerClassName="pb-[max(13px,env(safe-area-inset-bottom,0px))]"
                  />
                </SheetContent>
              </Sheet>
              <FeedBotMark className="size-7 shrink-0" label={t("app.name")} />
              <span className="h-6 w-px shrink-0 bg-white/[0.12]" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="m-0 truncate text-[10px] leading-4 font-[750] text-nav-section ltr:tracking-[0.08em] ltr:uppercase">
                  {t("app.account.workspace")}
                </p>
                <p className="m-0 truncate text-[14.5px] leading-5 font-[700] text-[#f2fff9]">
                  <bdi>{selected.name}</bdi>
                </p>
              </div>
            </header>
          ) : null}

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={routeTransitionKey(location.pathname)}
              className="flex min-h-0 min-w-0 flex-1 flex-col"
              initial="initial"
              animate="animate"
              exit="exit"
              variants={routeMotion.variants}
              transition={routeMotion.transition}
            >
              <main
                className={
                  isFullHeightWorkspace
                    ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                    : "flex flex-1 justify-center overflow-y-auto px-4 py-5 md:px-6 md:py-8 [scrollbar-gutter:stable]"
                }
              >
                {agentLockedOut ? (
                  <Navigate to="/inbox" replace />
                ) : organizationSuspended && !platformAdminAccess ? (
                  <OrganizationUnavailableState />
                ) : organizationSuspended ? (
                  <div
                    className={cn(
                      "flex min-h-0 min-w-0 flex-1 flex-col",
                      isFullHeightWorkspace ? "overflow-hidden" : "w-full",
                    )}
                  >
                    <AppNotice
                      severity="warning"
                      title={t("organization.suspended.title")}
                      description={t("organization.platformAdmin.suspendedNotice")}
                      testId="platform-admin-suspended-notice"
                      className={cn("shrink-0 rounded-none border-x-0 border-t-0", !isFullHeightWorkspace && "mb-4 rounded-lg border")}
                    />
                    {workspaceOutlet}
                  </div>
                ) : (
                  workspaceOutlet
                )}
              </main>
            </motion.div>
          </AnimatePresence>
          <NotificationStack
            onOpenImport={(jobId) => {
              navigate("/contacts");
              window.dispatchEvent(new CustomEvent("open-contact-import", { detail: { jobId } }));
            }}
          />
        </div>
      </div>
      <ContactImportJobPoller organizationId={selected.id} />
    </TooltipProvider>
    </InboxRealtimeProvider>
    </NotificationProvider>
  );
}
